import React from 'react';
import { Table, TableProps, Pagination, Skeleton } from 'antd';
import { spacing } from './tokens';

interface DataTableProps<T> extends Omit<TableProps<T>, 'pagination'> {
  pagination?:
    | {
        current: number;
        total: number;
        pageSize: number;
        onChange: (page: number) => void;
        showSizeChanger?: boolean;
        showQuickJumper?: boolean;
        showTotal?: (total: number, range: [number, number]) => string;
      }
    | false;
  emptyText?: string;
  showSkeletonOnLoad?: boolean;
  skeletonRows?: number;
}

export function DataTable<T extends Record<string, any>>({
  pagination,
  emptyText = 'Không có dữ liệu',
  showSkeletonOnLoad = false,
  skeletonRows = 5,
  loading,
  ...tableProps
}: DataTableProps<T>) {
  const tablePagination = pagination
    ? {
        position: ['bottomRight' as const],
        current: pagination.current,
        total: pagination.total,
        pageSize: pagination.pageSize,
        showSizeChanger: pagination.showSizeChanger ?? false,
        showQuickJumper: pagination.showQuickJumper ?? true,
        showTotal: pagination.showTotal,
        onChange: pagination.onChange,
      }
    : false;

  if (loading && showSkeletonOnLoad) {
    return (
      <Skeleton
        active
        paragraph={{ rows: skeletonRows }}
        title={false}
        style={{ padding: spacing.md }}
      />
    );
  }

  return (
    <>
      <Table<T>
        {...tableProps}
        pagination={tablePagination}
        locale={{ emptyText }}
        scroll={{ x: 'max-content' }}
        loading={loading}
        style={{
          ...tableProps.style,
        }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
        }
      />
      <style>{`
        .table-row-even:hover,
        .table-row-odd:hover {
          background-color: #f5f5f5 !important;
        }
        .ant-table-thead > tr > th {
          background-color: #fafafa;
          font-weight: 600;
          color: #262626;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f0f0f0;
        }
        .ant-table-tbody > tr:focus-within {
          outline: 2px solid #1890ff;
          outline-offset: -2px;
        }
        .ant-btn:focus-visible {
          outline: 2px solid #1890ff;
          outline-offset: 2px;
        }
      `}</style>
    </>
  );
}
